'use client';

/**
 * Client-Side Error Reporter
 *
 * ⚠️ CRITICAL: This file MUST have 'use client' directive!
 * It imports @sentry/nextjs which calls crypto.randomUUID() during initialization.
 * Without 'use client', this would be evaluated during SSR and break Next.js 16 PPR.
 *
 * Captures and sends client-side errors to the error reporting API.
 * Handles:
 * - Runtime errors (window.onerror, unhandledrejection)
 * - React Error Boundary errors
 * - Manual error capture
 *
 * Features:
 * - Rate limiting (max 5 errors per minute)
 * - Sentry integration for user context sync
 * - Breadcrumb integration
 * - Offline queuing (TODO: Phase 4)
 *
 * SIMPLIFIED: Sentry now handles browser/device detection automatically.
 * We only capture CRM-specific environment data (url, route, git commit).
 *
 * @see instrumentation-client.ts - Sentry handles browser/device detection
 *
 * Related: [[knowledge/bug-patterns]] (error-reporter self-throw on non-JSON response — Sentry 108656940)
 */

import * as Sentry from '@sentry/nextjs';
import { notifyStaleBundle } from '@/lib/utils/stale-bundle-recovery';
import type {
  ErrorReport,
  ErrorType,
  ErrorEnvironment,
  ClientErrorReportRequest,
  ClientErrorReportResponse,
} from '../types';
import { determineSeverity } from '../config';
import {
  getBreadcrumbs,
  clearBreadcrumbs,
  addBreadcrumb,
  getSessionSummary,
  getConnectionInfo,
} from './breadcrumbs';
import { detectIncident } from '@/lib/utils/incident-detection';
import { decodeReactError, isMinifiedReactError, getQuickDiagnostic } from '../react-error-decoder';
import { isNextControlFlowError } from '@/lib/utils/next-control-flow-errors';

// =============================================================================
// CONFIGURATION
// =============================================================================

const ERROR_REPORT_ENDPOINT = '/api/error-reports';
const INCIDENT_REPORT_ENDPOINT = '/api/system/incidents';
const MAX_ERRORS_PER_MINUTE = 5;
const RATE_LIMIT_WINDOW_MS = 60_000;

// =============================================================================
// USER CONTEXT (Set when user logs in, included in error reports)
// =============================================================================

/**
 * User context for error reports.
 * Set via setUserContext() when auth state changes.
 */
interface UserContext {
  id: string;
  email?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  organizationId?: string | null;
  role?: string | null;
}

/** Current user context (null if not logged in) */
let currentUserContext: UserContext | null = null;

/**
 * Set user context for error reports.
 * Call this when user logs in or auth state changes.
 *
 * Syncs with Sentry for unified user tracking across:
 * - Custom error reports (local database)
 * - Sentry errors (dashboard, replay)
 * - User feedback (screenshots, breadcrumbs)
 *
 * @param user - User context or null to clear
 *
 * @example
 * ```typescript
 * // On login
 * setUserContext({
 *   id: user.id,
 *   email: user.email,
 *   displayName: user.user_metadata?.full_name,
 *   avatarUrl: user.user_metadata?.avatar_url,
 *   organizationId: user.app_metadata?.organization_id,
 *   role: user.app_metadata?.role,
 * });
 *
 * // On logout
 * setUserContext(null);
 * ```
 */
export function setUserContext(user: UserContext | null): void {
  currentUserContext = user;

  // Sync with Sentry for unified user tracking
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username: user.displayName ?? undefined,
    });
    // Set CRM-specific context as tags (for filtering in Sentry dashboard)
    Sentry.setTag('organization_id', user.organizationId ?? 'unknown');
    Sentry.setTag('user_role', user.role ?? 'unknown');
  } else {
    // Clear Sentry user on logout
    Sentry.setUser(null);
    Sentry.setTag('organization_id', undefined);
    Sentry.setTag('user_role', undefined);
  }
}

/**
 * Get the current user context (for testing/debugging).
 */
export function getUserContext(): UserContext | null {
  return currentUserContext;
}

/**
 * Clear user context (convenience function for logout).
 */
export function clearUserContext(): void {
  currentUserContext = null;
}

// =============================================================================
// RATE LIMITING
// =============================================================================

/** Timestamps of recently sent errors */
let recentErrors: number[] = [];

/**
 * Check if we're rate limited.
 * Returns true if we've sent too many errors recently.
 */
function isRateLimited(): boolean {
  const now = Date.now();
  recentErrors = recentErrors.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
  return recentErrors.length >= MAX_ERRORS_PER_MINUTE;
}

/**
 * Record an error send for rate limiting.
 */
function recordErrorSend(): void {
  recentErrors.push(Date.now());
}

// =============================================================================
// ENVIRONMENT DETECTION (CRM-Specific Only)
// Note: Sentry automatically captures browser, OS, device type, screen size
// =============================================================================

/**
 * Build CRM-specific environment context.
 * Browser/device detection is handled by Sentry automatically.
 */
function buildEnvironment(): ErrorEnvironment {
  if (typeof window === 'undefined') {
    return {
      url: '',
      route: '',
      nodeEnv: process.env.NODE_ENV || 'unknown',
    };
  }

  return {
    url: window.location.href,
    route: window.location.pathname,
    // CRM-specific context (Sentry doesn't capture these)
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    nodeEnv: process.env.NODE_ENV || 'unknown',
    vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
    gitCommit: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 7),
    // Note: browser, browserVersion, os, deviceType, screenSize
    // are captured by Sentry automatically via instrumentation-client.ts
  };
}

// =============================================================================
// ERROR EXTRACTION
// =============================================================================

/**
 * Extended error details including React-specific decoding.
 */
interface ExtractedErrorDetails {
  message: string;
  name?: string;
  stack?: string;
  digest?: string;
  /** If this is a React error, contains decoded information */
  reactError?: {
    errorCode: number;
    args: string[];
    fullMessage: string;
    docsUrl: string;
    category: string;
    diagnostic: string | null;
  };
}

/**
 * Extract error details from various error types.
 * Automatically decodes minified React errors for better debugging.
 */
function extractErrorDetails(error: unknown): ExtractedErrorDetails {
  let message: string;
  let name: string | undefined;
  let stack: string | undefined;
  let digest: string | undefined;

  if (error instanceof Error) {
    message = error.message;
    name = error.name;
    stack = error.stack;
    digest = (error as Error & { digest?: string }).digest;
  } else if (typeof error === 'string') {
    message = error;
  } else if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    message = (obj.message as string) || JSON.stringify(error);
    name = obj.name as string | undefined;
  } else {
    message = String(error);
  }

  const result: ExtractedErrorDetails = { message, name, stack, digest };

  // Check if this is a minified React error and decode it
  if (isMinifiedReactError(message)) {
    const decoded = decodeReactError(message);
    if (decoded.isReactError && decoded.errorCode !== null) {
      result.reactError = {
        errorCode: decoded.errorCode,
        args: decoded.args,
        fullMessage: decoded.fullMessage,
        docsUrl: decoded.docsUrl || `https://react.dev/errors/${decoded.errorCode}`,
        category: decoded.errorCategory || 'React Error',
        diagnostic: getQuickDiagnostic(decoded.errorCode),
      };

      // IMPORTANT: Replace the minified message with the decoded full message
      // This is what will show up in the error report
      result.message = decoded.fullMessage;

      // Update the error name to include the React error code
      result.name = `React Error #${decoded.errorCode} (${decoded.errorCategory})`;
    }
  }

  return result;
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * React error info passed from ErrorBoundary or onRecoverableError.
 * All fields are optional — callers may have partial info depending on the error path.
 */
export interface ReactErrorInfo {
  /**
   * React component stack (render-tree path to the failing component).
   * Available from ErrorBoundary.componentDidCatch(error, errorInfo).componentStack
   * and from React 19 onRecoverableError(error, { componentStack }).
   */
  componentStack?: string;
  /**
   * React 19.2+ owner stack — the creator chain complementing componentStack.
   * Obtained via React.captureOwnerStack() synchronously inside an error handler.
   */
  ownerStack?: string;
  /** Next.js server-render digest for correlation with server logs */
  digest?: string;
}

/**
 * Capture and send a client-side error.
 *
 * @param error - The error to capture
 * @param errorType - Type of error (client_runtime, client_component)
 * @param metadata - Additional context
 * @param reactErrorInfo - Optional React error info with componentStack/ownerStack/digest.
 *   All existing callers continue to work unchanged (parameter is optional).
 * @returns Promise that resolves when the report is sent (or rejected if rate limited)
 *
 * @example
 * ```typescript
 * try {
 *   riskyOperation();
 * } catch (error) {
 *   await captureClientError(error, 'client_runtime', { context: 'user action' });
 * }
 *
 * // With React component context (e.g. from onRecoverableError):
 * captureClientError(error, 'client_runtime', {}, { componentStack, ownerStack });
 * ```
 */
export async function captureClientError(
  error: unknown,
  errorType: ErrorType = 'client_runtime',
  metadata?: Record<string, unknown>,
  reactErrorInfo?: ReactErrorInfo
): Promise<ClientErrorReportResponse> {
  // Rate limit check
  if (isRateLimited()) {
    // intentional: silently skip when rate limited, this is expected behavior
    return { success: false, error: 'Rate limited' };
  }

  // Transient network-error early-skip (Sentry issue 128314764).
  //
  // "Failed to fetch", "Load failed", AbortError, and offline variants are EXPECTED,
  // non-actionable browser blips produced by TanStack Query's onOnline auto-refetch
  // (fires on every offline->online flap across ~75 polling hooks).
  //
  // The isIgnorableNoise() list in installGlobalErrorHandlers() already suppresses
  // these for uncaught window.error / unhandledrejection events, but that filter is
  // NEVER reached by direct captureClientError callers (e.g. readFetch catch blocks,
  // action error boundaries, useOrphanLeads infinite-query refetch). Without this
  // guard, any catch-then-captureClientError site turns a benign network blip into a
  // CRITICAL Sentry event plus a /api/error-reports POST — generating noise storms.
  //
  // This is a NARROW early-skip: only exact message-string / name matches that are
  // universally non-actionable. Real application errors (auth failures, server 5xx,
  // logic bugs) are never transient and will not match.
  //
  // Callers that already guard at their call-site (readFetch isTransientNetworkError)
  // will never reach this branch — belt-and-suspenders only.
  if (isCaptureTransientNoiseError(error)) {
    return { success: false, error: 'Transient network error (skipped)' };
  }

  // Next.js internal render-bailout / control-flow signal early-skip.
  //
  // A server-rendered digest (DYNAMIC_SERVER_USAGE, BAILOUT_TO_CLIENT_SIDE_RENDERING,
  // NEXT_HTTP_ERROR_FALLBACK) survives the client/server serialization boundary on
  // the thrown Error's `.digest` property, so error.tsx / global-error.tsx's
  // captureComponentError -> captureClientError can receive it directly as `error`.
  // This is framework control flow, not an application bug — belt-and-braces
  // alongside the server-side skip in error-logger.ts (see
  // next-control-flow-errors.ts for the shared classifier + full incident).
  if (isNextControlFlowError(error)) {
    return { success: false, error: 'Next.js control-flow signal (skipped)' };
  }

  try {
    const {
      message,
      name,
      stack,
      digest: extractedDigest,
      reactError,
    } = extractErrorDetails(error);
    // Prefer digest from reactErrorInfo (caller-provided) over extracted one
    const digest = reactErrorInfo?.digest ?? extractedDigest;
    const severity = determineSeverity(message, errorType);

    // Merge incoming componentStack/ownerStack for Sentry's native React context.
    // Sentry UI shows contexts.react.componentStack in a dedicated "React" section —
    // much more visible than a generic "extra" blob.
    const componentStack = reactErrorInfo?.componentStack;
    const ownerStack = reactErrorInfo?.ownerStack;

    // Register componentStack in the instrumentation-client.ts WeakMap side-channel
    // so the Sentry event processor can attach it to the event as contexts.react even
    // when Sentry captures the error through a different code path (e.g. global handler).
    if (componentStack && error instanceof Error) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (globalThis as any).__sentryRegisterReactComponentStack?.(error, componentStack);
      } catch {
        // Silently ignore — registration is best-effort
      }
    }

    // ALSO send to Sentry for proper monitoring (in addition to custom DB)
    // This ensures errors show up in Sentry dashboard even when caught.
    // When componentStack is available, use withScope to set contexts.react so
    // Sentry displays it in the native "React Component Stack" section.
    if (componentStack) {
      Sentry.withScope((scope) => {
        scope.setContext('react', { componentStack, ownerStack: ownerStack ?? null });
        // Set level on scope (not in hint) — scope.captureException hint type is EventHint
        // which does not accept `level`. Use scope.setLevel() instead (Sentry v8+ API).
        scope.setLevel(
          severity === 'critical' ? 'fatal' : severity === 'error' ? 'error' : 'warning'
        );
        scope.setTag('errorType', errorType);
        if (metadata?.component) {
          scope.setTag('component', metadata.component as string);
        }
        scope.captureException(error instanceof Error ? error : new Error(message), {
          data: {
            ...metadata,
            reactError,
            digest,
            ownerStack: ownerStack ?? undefined,
          },
        });
      });
    } else {
      Sentry.captureException(error instanceof Error ? error : new Error(message), {
        level: severity === 'critical' ? 'fatal' : severity === 'error' ? 'error' : 'warning',
        tags: {
          errorType,
          component: metadata?.component as string | undefined,
        },
        extra: {
          ...metadata,
          reactError,
          digest,
        },
      });
    }

    // Stale bundle? Report is already in Sentry — show user-controlled refresh toast.
    // This covers errors caught by try-catch that never reach ServerActionRecovery.
    if (notifyStaleBundle(message)) {
      return { success: true };
    }

    // Build enhanced metadata with React error info if present
    const enhancedMetadata: Record<string, unknown> = {
      ...metadata,
    };

    // If this is a React error, add decoded info to metadata for better debugging
    if (reactError) {
      enhancedMetadata.reactError = reactError;
      // Also add a breadcrumb for the React error diagnostic
      if (reactError.diagnostic) {
        addBreadcrumb(
          'custom',
          'React Error Diagnostic',
          {
            errorCode: reactError.errorCode,
            category: reactError.category,
            diagnostic: reactError.diagnostic,
            docsUrl: reactError.docsUrl,
            args: reactError.args,
          },
          'error'
        );
      }
    }

    // Capture HTML snapshot for hydration errors on ALL paths — including the
    // window.error path (the 128 real events in JAVASCRIPT-NEXTJS-BF) where
    // captureComponentError is never reached and componentStack is unavailable.
    // This is our only diagnostic signal for recoverable hydration mismatches.
    captureHtmlSnapshotIfHydrationError(error, enhancedMetadata);

    const report: ErrorReport = {
      errorType,
      severity,
      errorMessage: message,
      errorName: name,
      stackTrace: stack,
      digest,
      // React component context — top-level fields so the API route can forward them
      // to Sentry's contexts.react and the DB can query them independently of metadata.
      componentStack: componentStack?.slice(0, 20000),
      ownerStack: ownerStack?.slice(0, 10000),
      environment: buildEnvironment(),
      breadcrumbs: getBreadcrumbs(),
      metadata: enhancedMetadata,
      // User context (if logged in)
      userId: currentUserContext?.id,
      userEmail: currentUserContext?.email ?? undefined,
      userDisplayName: currentUserContext?.displayName ?? undefined,
      userAvatarUrl: currentUserContext?.avatarUrl ?? undefined,
      organizationId: currentUserContext?.organizationId ?? undefined,
      userRole: currentUserContext?.role ?? undefined,
      // Enhanced context
      sessionSummary: getSessionSummary(),
      connectionInfo: getConnectionInfo(),
    };

    // Send to API
    const response = await sendReport(report);

    // Clear breadcrumbs after successful capture
    if (response.success) {
      clearBreadcrumbs();
      recordErrorSend();
    }

    // Also check if this looks like a service outage and report to incident system.
    // Pass name + digest so server can spike-track deploy faults even without a
    // matching pattern (digest signals server-component render origin).
    // This is fire-and-forget (non-blocking)
    reportPotentialIncident(message, { name, digest }).catch(() => {});

    return response;
  } catch (sendError) {
    // Guard: network-abort errors on the /api/error-reports transport path (e.g. Safari
    // "Load failed", Chrome "Failed to fetch", AbortError) are NOT app bugs — they happen
    // when the user navigates away while the telemetry POST is in-flight. Forwarding them
    // to Sentry.captureException creates a self-referential feedback loop (Sentry issue
    // 108973768). Swallow silently; use sendBeacon as the delivery fallback instead.
    if (isErrorReportTransportAbort(sendError)) {
      return { success: false, error: 'Network error (transport abort)' };
    }
    // Capture to Sentry instead of console.error (meta-error: error reporting failed)
    Sentry.captureException(sendError, {
      extra: { context: 'Failed to send error report', originalErrorType: errorType },
    });
    return { success: false, error: 'Failed to send report' };
  }
}

/**
 * Capture HTML snapshot for React hydration mismatch errors (#418).
 * Without a snapshot the issue deduplicates across releases and can't be
 * root-caused from the Sentry payload alone (see Sentry JAVASCRIPT-NEXTJS-5A).
 * Shared by both captureComponentError and the onRecoverableError path.
 *
 * @internal
 */
function captureHtmlSnapshotIfHydrationError(error: unknown, meta: Record<string, unknown>): void {
  if (
    typeof document !== 'undefined' &&
    error instanceof Error &&
    (error.message.includes('Minified React error #418') ||
      error.message.includes('Minified React error #419') ||
      error.message.includes('Minified React error #421') ||
      error.message.includes('Minified React error #423'))
  ) {
    // Target document.body, NOT document.documentElement. The first 2000 chars of <html>
    // is almost entirely <head>/<meta>/<link> tags — zero mismatch signal. The mismatched
    // element is always inside <body>. 8000 chars gives enough body content to capture the
    // relevant component tree while staying within the API route's metadata budget.
    meta.htmlSnapshot = (document.body ?? document.documentElement).outerHTML.slice(0, 8000);
    // Record the route so Sentry viewers know which page produced the snapshot.
    if (typeof window !== 'undefined' && window.location?.pathname) {
      meta.htmlSnapshotUrl = window.location.pathname;
    }
  }
}

/**
 * Capture a React Error Boundary error.
 * Includes component stack from errorInfo and sets Sentry's native
 * contexts.react.componentStack so it appears in the "React" section of
 * Sentry events (not just as a generic extra blob).
 *
 * @param error - The error caught by the boundary
 * @param errorInfo - React error info with componentStack
 * @param metadata - Additional context
 */
export async function captureComponentError(
  error: unknown,
  errorInfo?: { componentStack?: string; ownerStack?: string },
  metadata?: Record<string, unknown>
): Promise<ClientErrorReportResponse> {
  // Add component stack as breadcrumb for quick Sentry breadcrumb visibility
  if (errorInfo?.componentStack) {
    addBreadcrumb(
      'custom',
      'React component stack',
      {
        componentStack: errorInfo.componentStack.slice(0, 500),
      },
      'error'
    );
  }

  const enhancedMeta: Record<string, unknown> = {
    ...metadata,
    // Keep in metadata for DB backward-compat (existing queries may read from here)
    componentStack: errorInfo?.componentStack?.slice(0, 1000),
  };

  // Capture HTML snapshot for React #418/#419/#421/#423 hydration mismatches.
  captureHtmlSnapshotIfHydrationError(error, enhancedMeta);

  // Pass reactErrorInfo as the 4th arg so captureClientError:
  // (a) sets Sentry's contexts.react.componentStack via withScope (native Sentry UI section)
  // (b) populates top-level ErrorReport.componentStack for the API route → DB
  const reactErrorInfo: ReactErrorInfo | undefined =
    errorInfo?.componentStack || errorInfo?.ownerStack
      ? {
          componentStack: errorInfo.componentStack,
          ownerStack: errorInfo.ownerStack,
        }
      : undefined;

  return captureClientError(error, 'client_component', enhancedMeta, reactErrorInfo);
}

// =============================================================================
// GLOBAL ERROR HANDLERS
// =============================================================================

/** Whether global handlers are installed */
let globalHandlersInstalled = false;

/**
 * Install global error handlers for uncaught errors.
 * Call once at app initialization.
 *
 * @returns Cleanup function to remove handlers
 */
export function installGlobalErrorHandlers(): () => void {
  if (typeof window === 'undefined' || globalHandlersInstalled) {
    return () => {};
  }

  globalHandlersInstalled = true;

  // Known-benign error patterns that should NOT be POSTed to /api/error-reports.
  // Mirrors the `ignoreErrors` list in instrumentation-client.ts so the same
  // noise is filtered at both reporting paths (Sentry SDK and our custom API).
  // Add new entries here AND in instrumentation-client.ts to keep them in sync.
  const isIgnorableNoise = (msg: string): boolean => {
    if (!msg) return false;
    return (
      // Slate/Plate transient DOM<->model desync during onDOMBeforeInput.
      // Auto-recovers via forced rerender; no user-facing impact.
      // See Sentry issue 115334934.
      msg.includes('Cannot resolve a Slate point from DOM point') ||
      // Sibling of the above, thrown by DOMEditor.toSlateNode in the debounced
      // scheduleOnDOMSelectionChange path (slate-react editable.tsx) when a
      // <span>/<div>/<svg> is no longer in Slate's ELEMENT_TO_NODE WeakMap by
      // the time the 0ms debounce flushes (Plate floating menus / mention
      // chips / browser-extension DOM rewrites). Slate auto-recovers on the
      // next selection change. Sentry issues 119622903 + 119622917 (/tasks).
      msg.includes('Cannot resolve a Slate node from DOM node') ||
      msg.includes('ResizeObserver loop limit exceeded') ||
      msg.includes('ResizeObserver loop completed with undelivered notifications') ||
      // Realtime channel retry-heartbeat — three message shapes covered. See
      // instrumentation-client.ts for full context.
      // Sentry issues 119363898, 108095699, 118044119, 119771170, 119766741.
      msg.includes('still retrying') ||
      /failed after \d+ retries/.test(msg) ||
      // React Hooks errors — sibling fix; both #310 and #311 are stale-bundle
      // / hot-replace noise that ServerActionRecovery + VersionUpdateBanner
      // already handle. Sentry-side ignoreErrors had #310 but missed the
      // custom /api/error-reports path; added here for parity.
      // Sentry issues 105130517 (#310), 119813645 (#311).
      msg.includes('Rendered fewer hooks than expected') ||
      msg.includes('Rendered more hooks than during the previous render') ||
      // Network-abort errors from our own /api/error-reports telemetry delivery.
      // When captureClientError's fetch POST to /api/error-reports is cancelled
      // (page navigation, connection drop, Safari NSURLErrorCancelled), the
      // rejected promise surfaces here via unhandledrejection. Re-entering
      // captureClientError with that rejection would create a self-referential
      // feedback loop (Sentry issue 108973768). Mirror isErrorReportTransportAbort
      // message strings here so the global handler ignores them before they enter
      // the report pipeline.
      msg === 'Load failed' ||
      msg === 'Failed to fetch' ||
      msg === 'cancelled' ||
      msg.includes('The Internet connection appears to be offline')
    );
  };

  // Handle uncaught errors
  const handleError = (event: ErrorEvent) => {
    const errorMessage = event.error?.message || event.message || String(event.error);

    if (isIgnorableNoise(errorMessage)) return;

    // Report to normal error tracking
    captureClientError(event.error || event.message, 'client_runtime', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    }).catch(() => {
      // Silently ignore
    });

    // Also report to incident system if it looks like an outage (fire-and-forget)
    reportPotentialIncident(errorMessage).catch(() => {});
  };

  // Handle unhandled promise rejections
  const handleRejection = (event: PromiseRejectionEvent) => {
    const errorMessage = event.reason?.message || String(event.reason);

    if (isIgnorableNoise(errorMessage)) return;

    // Report to normal error tracking
    captureClientError(event.reason, 'client_runtime', {
      type: 'unhandledrejection',
    }).catch(() => {
      // Silently ignore
    });

    // Also report to incident system if it looks like an outage (fire-and-forget)
    reportPotentialIncident(errorMessage).catch(() => {});
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleRejection);
    globalHandlersInstalled = false;
  };
}

// =============================================================================
// API COMMUNICATION
// =============================================================================

/**
 * Report an error to the incident system if it looks like an outage.
 * This is fire-and-forget - we don't wait for the response.
 */
async function reportPotentialIncident(
  errorMessage: string,
  errorDetails?: { name?: string; digest?: string }
): Promise<void> {
  try {
    // Always attempt to detect a known outage pattern first.
    const incident = detectIncident(errorMessage);

    // For unrecognised patterns, still POST when the error originates from the
    // server-component render pipeline (digest present, or stack points at Next.js
    // internals). The server-side spike detector will decide whether to create an
    // incident. This is how deploy faults (e.g. jsdom ESM failure) get caught even
    // when the client only sees a generic React hydration error.
    const isServerRenderError =
      !!errorDetails?.digest ||
      /server-components-payload|_next\/server\/chunks|externalRequire/i.test(errorMessage);

    if (!incident?.isLikelyOutage && !isServerRenderError) {
      return; // Not an outage pattern and not a server-render origin — skip
    }

    // Build the error field: combine message, name, and digest for maximum signal.
    const errorField = [
      errorDetails?.name && errorDetails.name !== 'Error' ? errorDetails.name + ': ' : '',
      errorMessage,
      errorDetails?.digest ? ` [digest:${errorDetails.digest}]` : '',
    ]
      .join('')
      .slice(0, 10000);

    // Report to incident system (fire-and-forget)
    await fetch(INCIDENT_REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error: errorField,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
    });
  } catch (_) {
    // Silently fail - we can't report if reporting is broken
  }
}

/**
 * Early-skip guard for transient network blips passed to captureClientError directly
 * (Sentry issue 128314764).
 *
 * Matches the same set of benign, non-actionable network errors as isIgnorableNoise()
 * in installGlobalErrorHandlers(), extending coverage to ALL captureClientError callers,
 * not just the global uncaught-error handlers.
 *
 * CLASS: transient-network-error-reported-as-critical-sentry-noise
 *
 * SCOPE: deliberately narrow — exact message/name matches universally emitted by the
 * browser for offline/abort conditions. Real errors (auth, 5xx, logic bugs) never match.
 *
 * navigator.onLine check: also catches the case where TanStack Query fires an auto-refetch
 * immediately after connectivity is restored and the OS has not yet fully re-established
 * the network stack — fetch() will throw even though onLine=true is imminent.
 */
function isCaptureTransientNoiseError(err: unknown): boolean {
  // navigator.onLine === false (explicit false) means the browser KNOWS we are offline.
  // Do not use !navigator.onLine: when onLine is undefined (test envs, SSR) that
  // would incorrectly suppress every error.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!(err instanceof Error)) return false;
  const msg = err.message ?? '';
  const name = err.name ?? '';
  return (
    // Chrome/Firefox — standard "Failed to fetch" on network abort or offline
    msg === 'Failed to fetch' ||
    // Safari/WebKit — exact string WebKit throws for a cancelled/aborted network request
    msg === 'Load failed' ||
    // AbortController-triggered cancellation (any browser)
    name === 'AbortError' ||
    // Safari NSURLErrorCancelled lowercase variant
    msg === 'cancelled' ||
    // Safari offline variant (full message)
    msg.includes('The Internet connection appears to be offline') ||
    // Chrome/Firefox generic network-level failures from OS network stack
    msg.includes('NetworkError') ||
    msg.includes('network error') ||
    msg.includes('ERR_NETWORK') ||
    msg.includes('ERR_INTERNET_DISCONNECTED')
  );
}

/**
 * Detect network-abort errors on the error-reporting delivery path that must be
 * swallowed silently. These are NOT app bugs — they are transient transport failures:
 *
 * - Safari/WebKit: "Load failed" (fetch aborted during page navigation or connection drop)
 * - Chrome/Firefox: "Failed to fetch" (same network-abort class)
 * - AbortController: DOMException name="AbortError"
 *
 * WHY this guard exists: captureClientError → sendReport → fetch('/api/error-reports')
 * can fail with "Load failed" when the user navigates away mid-send. That TypeError
 * must NEVER be forwarded to Sentry.captureException — doing so creates a self-referential
 * feedback loop (telemetry delivery failure reported as an app error = Sentry issue 108973768).
 *
 * SCOPE: deliberately narrow — only exact message strings on the transport path.
 * Never use this to suppress real application errors.
 *
 * Exported so sibling fetch helpers (e.g. readFetch) can reuse the same guard
 * rather than maintaining independent copies. Sentry issue 128373241.
 */
export function isErrorReportTransportAbort(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message ?? '';
  const name = err.name ?? '';
  return (
    // Safari/WebKit — exact string WebKit throws for a cancelled/aborted network request
    msg === 'Load failed' ||
    // Chrome/Firefox — standard "Failed to fetch" on network abort or CORS failure
    msg === 'Failed to fetch' ||
    // AbortController-triggered cancellation (any browser)
    name === 'AbortError' ||
    // Safari offline variant
    msg.includes('The Internet connection appears to be offline') ||
    // Safari NSURLErrorCancelled lowercase variant
    msg === 'cancelled' ||
    // Node/undici request-stream abort surfaced meta-error text seen in
    // error_report_failures ("Unknown Error / Unexpected end of JSON input /
    // aborted") — the bare message Node's http module throws when the
    // underlying connection drops mid-request (code ECONNRESET).
    msg === 'aborted'
  );
}

/**
 * Send an error report to the API endpoint.
 *
 * Network failures (ad-blockers, offline) are silently discarded.
 * A failure to report an error must NEVER itself throw or generate a new Sentry event.
 * sendBeacon is used as a fire-and-forget fallback when fetch fails.
 */
async function sendReport(report: ErrorReport): Promise<ClientErrorReportResponse> {
  const request: ClientErrorReportRequest = { report };
  const body = JSON.stringify(request);

  try {
    const response = await fetch(ERROR_REPORT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      // Don't include credentials to avoid cookie issues
      credentials: 'same-origin',
    });

    if (!response.ok) {
      const text = await response.text().catch(() => 'Unknown error');
      return { success: false, error: `HTTP ${response.status}: ${text.slice(0, 200)}` };
    }

    // Defense-in-depth: never call response.json() blindly. A 200 response that is
    // not JSON (e.g. a platform/proxy plain-text page like "Request Entity Too Large"
    // from Vercel's serverless body-limit layer, or an HTML interstitial) would throw
    // a SyntaxError here — and because this transport is the error-reporter itself,
    // that SyntaxError gets re-reported via captureClientError, creating a recursive
    // meta-error (Sentry issue 108656940). Parse defensively and fail soft.
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return { success: false, error: 'Non-JSON response from error-reports endpoint' };
    }
    try {
      return (await response.json()) as ClientErrorReportResponse;
    } catch {
      // Malformed/empty JSON body despite a JSON content-type — fail soft, never throw.
      return { success: false, error: 'Malformed JSON response from error-reports endpoint' };
    }
  } catch {
    // Silently discard network errors (ad-blockers, offline, CORS).
    // Try sendBeacon as a fire-and-forget fallback — it survives page unloads
    // and bypasses some ad-blockers that block XMLHttpRequest/fetch.
    try {
      navigator.sendBeacon?.(ERROR_REPORT_ENDPOINT, new Blob([body], { type: 'application/json' }));
    } catch {
      // Final fallback: truly silent
    }
    return { success: false, error: 'Network error' };
  }
}

// =============================================================================
// USER-REPORTED ERRORS
// =============================================================================

/**
 * Submit a user-reported error/issue.
 * Captures current breadcrumbs to show what the user was doing before reporting.
 *
 * @param description - User's description of the issue
 * @param metadata - Additional context
 * @returns Promise with the response
 *
 * @example
 * ```typescript
 * await captureUserReport('Form is not saving properly');
 * ```
 */
export async function captureUserReport(
  description: string,
  metadata?: Record<string, unknown>
): Promise<ClientErrorReportResponse> {
  // Rate limit check
  if (isRateLimited()) {
    // intentional: silently skip when rate limited, this is expected behavior
    return { success: false, error: 'Rate limited' };
  }

  try {
    const report: ErrorReport = {
      errorType: 'user_reported',
      severity: 'warning', // User reports are warnings by default
      errorMessage: description,
      errorName: 'UserReport',
      environment: buildEnvironment(),
      breadcrumbs: getBreadcrumbs(),
      metadata: {
        ...metadata,
        userReportedAt: new Date().toISOString(),
      },
      // User context (if logged in)
      userId: currentUserContext?.id,
      userEmail: currentUserContext?.email ?? undefined,
      userDisplayName: currentUserContext?.displayName ?? undefined,
      userAvatarUrl: currentUserContext?.avatarUrl ?? undefined,
      organizationId: currentUserContext?.organizationId ?? undefined,
      userRole: currentUserContext?.role ?? undefined,
      // Enhanced context
      sessionSummary: getSessionSummary(),
      connectionInfo: getConnectionInfo(),
    };

    // Send to API
    const response = await sendReport(report);

    // Clear breadcrumbs after successful capture
    if (response.success) {
      clearBreadcrumbs();
      recordErrorSend();
    }

    return response;
  } catch (sendError) {
    // Same transport-abort guard as captureClientError — swallow network delivery
    // failures silently; they are not user-report bugs.
    if (isErrorReportTransportAbort(sendError)) {
      return { success: false, error: 'Network error (transport abort)' };
    }
    // Capture to Sentry instead of console.error (meta-error: error reporting failed)
    Sentry.captureException(sendError, {
      extra: { context: 'Failed to send user report' },
    });
    return { success: false, error: 'Failed to send report' };
  }
}

// =============================================================================
// MANUAL BREADCRUMB HELPERS
// =============================================================================

/**
 * Add a custom breadcrumb from application code.
 * Re-export from breadcrumbs for convenience.
 */
export { addBreadcrumb, getBreadcrumbs, clearBreadcrumbs } from './breadcrumbs';
